import React from 'react';

export type IconName =
  | 'home'
  | 'chart'
  | 'flag'
  | 'list'
  | 'user'
  | 'cog'
  | 'users'
  | 'trophy'
  | 'calendar'
  | 'calculator'
  | 'sun'
  | 'moon'
  | 'computer'
  | 'file-text'
  | 'building'
  | 'monitor'
  | 'credit-card'
  | 'layers'
  | 'envelope'
  | 'whistle';

interface NavIconProps {
  name: IconName;
  className?: string;
  'aria-hidden'?: boolean;
}

export function NavIcon({ name, className = 'h-5 w-5', ...props }: NavIconProps) {
  const commonProps = {
    xmlns: 'http://www.w3.org/2000/svg',
    viewBox: '0 0 24 24',
    className,
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': props['aria-hidden'] ?? true,
  };

  switch (name) {
    case 'home':
      return (
        <svg {...commonProps}>
          <path d="M3 10.5 12 3l9 7.5V21a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z" />
        </svg>
      );

    case 'chart':
      return (
        <svg {...commonProps}>
          <path d="M3 20h18" />
          <path d="M7 16v-6" />
          <path d="M12 16V8" />
          <path d="M17 16v-4" />
        </svg>
      );

    case 'flag':
      return (
        <svg {...commonProps}>
          <path d="M4 3v18" />
          <path d="M4 4h12l-2 4 2 4H4" />
        </svg>
      );

    case 'list':
      return (
        <svg {...commonProps}>
          <path d="M8 6h12" />
          <path d="M4 6h.01" />
          <path d="M8 12h12" />
          <path d="M4 12h.01" />
          <path d="M8 18h12" />
          <path d="M4 18h.01" />
        </svg>
      );

    case 'user':
      return (
        <svg {...commonProps}>
          <path d="M12 12a5 5 0 1 0-5-5 5 5 0 0 0 5 5Z" />
          <path d="M3 21a9 9 0 0 1 18 0" />
        </svg>
      );

    case 'cog':
      // Settings gear icon (Lucide-inspired), cleaner and more balanced
      return (
        <svg {...commonProps}>
          <path d="M12 9a3 3 0 1 0 3 3 3 3 0 0 0-3-3Z" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06A1.65 1.65 0 0 0 15 19.4a1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09A1.65 1.65 0 0 0 15 4.6a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
        </svg>
      );

    case 'users':
      return (
        <svg {...commonProps}>
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      );

    case 'trophy':
      return (
        <svg {...commonProps}>
          <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
          <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
          <path d="M4 22h16" />
          <path d="M10 14.66V17c0 1.1.9 2 2 2s2-.9 2-2v-2.34" />
          <path d="M12 14.66V9c0-1.1-.9-2-2-2H8" />
          <path d="M12 14.66V9c0 1.1.9 2 2 2h2" />
        </svg>
      );

    case 'calendar':
      return (
        <svg {...commonProps}>
          <path d="M8 2v4" />
          <path d="M16 2v4" />
          <rect width="18" height="18" x="3" y="4" rx="2" />
          <path d="M3 10h18" />
        </svg>
      );

    case 'calculator':
      return (
        <svg {...commonProps}>
          <rect width="18" height="20" x="3" y="2" rx="2" />
          <path d="M7 6h10" />
          <path d="M7 10h10" />
          <path d="M7 14h10" />
          <path d="M7 18h10" />
        </svg>
      );

    case 'sun':
      return (
        <svg {...commonProps}>
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2" />
          <path d="M12 20v2" />
          <path d="m4.93 4.93 1.41 1.41" />
          <path d="m17.66 17.66 1.41 1.41" />
          <path d="M2 12h2" />
          <path d="M20 12h2" />
          <path d="m6.34 17.66-1.41 1.41" />
          <path d="m19.07 4.93-1.41 1.41" />
        </svg>
      );

    case 'moon':
      return (
        <svg {...commonProps}>
          <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
        </svg>
      );

    case 'computer':
      return (
        <svg {...commonProps}>
          <rect width="20" height="14" x="2" y="3" rx="2" ry="2" />
          <line x1="8" x2="16" y1="21" y2="21" />
          <line x1="12" x2="12" y1="17" y2="21" />
        </svg>
      );

    case 'file-text':
      return (
        <svg {...commonProps}>
          <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
          <polyline points="14,2 14,8 20,8" />
          <line x1="16" x2="8" y1="13" y2="13" />
          <line x1="16" x2="8" y1="17" y2="17" />
          <line x1="10" x2="8" y1="9" y2="9" />
        </svg>
      );

    case 'building':
      return (
        <svg {...commonProps}>
          <rect width="16" height="20" x="4" y="2" rx="2" ry="2" />
          <path d="M9 9h1v1H9z" />
          <path d="M14 9h1v1h-1z" />
          <path d="M9 14h1v1H9z" />
          <path d="M14 14h1v1h-1z" />
          <path d="M9 19h1v1H9z" />
          <path d="M14 19h1v1h-1z" />
        </svg>
      );

    case 'monitor':
      return (
        <svg {...commonProps}>
          <rect width="20" height="14" x="2" y="3" rx="2" ry="2" />
          <line x1="8" x2="16" y1="21" y2="21" />
          <line x1="12" x2="12" y1="17" y2="21" />
        </svg>
      );

    case 'credit-card':
      return (
        <svg {...commonProps}>
          <rect width="20" height="14" x="2" y="5" rx="2" ry="2" />
          <line x1="2" x2="22" y1="10" y2="10" />
        </svg>
      );

    case 'layers':
      return (
        <svg {...commonProps}>
          <path d="m12 2 10 6.5-10 6.5L2 8.5Z" />
          <path d="m2 15.5 10 6.5 10-6.5" />
          <path d="m2 12 10 6.5L22 12" />
        </svg>
      );

    case 'envelope':
      return (
        <svg {...commonProps}>
          <rect width="20" height="16" x="2" y="4" rx="2" />
          <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
        </svg>
      );

    case 'whistle':
      return (
        <svg {...commonProps}>
          <path d="M4 12a6 6 0 0 0 6 6h2a5 5 0 0 0 5-5v-2H8v-2h12l1-2-3-3h-4l-2 2H9a5 5 0 0 0-5 5z" />
          <circle cx="16" cy="13" r="1" />
        </svg>
      );

    default:
      return <span aria-hidden className={className} />;
  }
}
